import { find, map, get } from 'lodash';
import { ValueType, EXTERNAL_TYPES } from './Variable';



export const DEMANDS_AI_PROMPT = {
  system: `You are an experienced personal injury attorney who is an expert in writing demand letters for law firms. Specifically, you are a specialized drafting consultant for law firms. You are given an example or examples of a law firm's customary language for a section of their demand letters from their past cases. Then, you are given details of a new case, and you use the style, format, tone, voice, purpose, structure, word choice, and types of information included in the examples to draft the corresponding section for a demand letter reflecting the details of the new case. The sections that you draft apply the style, format, tone, voice, purpose, structure, word choice, and types of information included in the examples but use the details of the new case to create an effective section of the demand letter for the law firm.
  When you draft a section, the section should emulate the style, format, tone, voice, purpose, structure, word choice, and types of information included in the example sections, which are placed in the <example> </example> XML tags.
  If an item of case details begins with "[+fv", consider that entry as indicating that there is no information for that item, and that such item should be omitted from the section and not considered during the drafting of the section. "[+fv" is appearing because there was nothing available to be pulled from the data set for that item of case details.
  Read the details and the examples carefully before drafting the section because the section you draft will reflect the style, format, tone, voice, purpose, structure, and word choice of the examples and the facts of the details.
  Only include the details in your draft which are relevant for the particular section being drafted, which you determine as relevant through a comprehensive review of the examples and the types of information which they discuss. If a type of information is included with the details, but the examples do not reference or discuss that type of information, do NOT include it in your section.`,
  user: `Carefully and thoroughly review the details placed in the <details></details> XML tags and the style, format, tone, voice, purpose, structure, word choice, and types of information included in the examples placed in the <example></example> XML tags.
  Referencing the details placed in the <details></details> XML tags and the style, format, tone, voice, purpose, structure, word choice, and types of information included in the examples placed in the <example></example> XML tags, draft the corresponding section of the demand letter.
  Think step by step within <thinking></thinking> XML tags. Then, draft the section within <draft></draft> XML tags.
  For the factual details of the incident, use ONLY the details placed in the <details></details> XML tags, and do not use any of the facts from the examples. Refer to the examples ONLY for tailoring the style, format, tone, voice, purpose, structure, word choice, and types of information of the section and NOT for the facts, details, or information of the incident.`,
}

export const AI_ENGINES = [
  {
    title: 'Open AI',
    key: 'openAI',
    description: '',
    model: 'gpt-3.5-turbo-16k',
    systemPrompt: true,
  },
  {
    title: 'Anthropic',
    key: 'anthropic',
    description: '',
    model: 'claude-2.1',
    systemPrompt: true,
  }
];

export const STEP_TYPES = {
  SYSTEM: 'system',
  USER: 'user',
  ASSISTANT: 'assistant',
  OUTLAW_USER: 'outlaw',
}
//Outlaw user is the AI instruction step. 
//We will use this step to append onto the user step.

const DEMANDS_PROMPT = [
  {role: STEP_TYPES.SYSTEM, content: DEMANDS_AI_PROMPT[STEP_TYPES.SYSTEM], adminOnly: true, step: 'System Prompt'},
  {role: STEP_TYPES.USER, content: DEMANDS_AI_PROMPT[STEP_TYPES.USER], adminOnly: true, step: 'User'},
  {role: STEP_TYPES.OUTLAW_USER, content: '', adminOnly: false, step: 'Additional AI Instructions (Optional)'},
]

//Empty prompt for legacy/total customizable blocks.
const DEFAULT_PROMPT = [
  {role: STEP_TYPES.SYSTEM, content: '', adminOnly: true, step: 'System Prompt'},
  {role: STEP_TYPES.USER, content: '', adminOnly: true, step: 'User'},
  {role: STEP_TYPES.OUTLAW_USER, content: '', adminOnly: false, step: 'AI Instructions'},
]
 
//Will be pulled from DB along with the structure for v2.
export const BLOCK_TYPE = {
  DEFAULT: 'default',
  DEMANDS: 'demands',
}

export const BLOCK_PROMPT = {
  'default': DEFAULT_PROMPT,
  'demands': DEMANDS_PROMPT 
}

export const RESPONSE_OPTIONS = [1, 2, 3];


export const togglePromptType = (type, currentPrompt) => {
  //we want to maintain the current instructions when we toggle.
  const currentInstruction = find(currentPrompt, {role: STEP_TYPES.OUTLAW_USER});

  const newPrompt = map(BLOCK_PROMPT[type], (step) => {
    if(step.role === STEP_TYPES.OUTLAW_USER) step.content = currentInstruction.content;
    return step;
  });
  
  return newPrompt;
}

//This has to occure outside the constructor because we build workflow and children post section creation.
export const buildLegacyPrompt = (list, children, workflow) => {
    const aiPrompt = {}
    aiPrompt.actionName = get(list, 'actionName', 'Generate');
    aiPrompt.responseOptions = 1;
    if (workflow) {
      //This means its a demand setup
      aiPrompt.prompt = workflow.serviceProviders ? DEMANDS_PROMPT : DEFAULT_PROMPT;
      aiPrompt.engine = workflow.aiEngine || AI_ENGINES[0].key;
      aiPrompt.type = workflow.serviceProviders ? BLOCK_TYPE.DEMANDS : BLOCK_TYPE.DEFAULT;
    }
    else {
      aiPrompt.prompt = DEFAULT_PROMPT;
      aiPrompt.engine = AI_ENGINES[0].key;
      aiPrompt.type = BLOCK_TYPE.DEFAULT;
    }

    //add previous content to the model.
    if (list.prompt) {
      aiPrompt.prompt = map(aiPrompt.prompt, (step) => {
        if(step.role === STEP_TYPES.OUTLAW_USER) step.content = list.prompt;
        return step;
      });
    }

    if (children.length > 0 ) {
      const linkedIDs = map(children, 'id');
      aiPrompt.linkedSections = linkedIDs.join('|');
    }


    return aiPrompt;
}

//V2
//Outlaw admins will build prompts and save them at a universal level in outlaw. (They will enable prompts based on some criteria)
//Preconfiged Prompts will be selected on the template/deal for use. (Always hidden though)
//

export default class AIPrompt {
  engine = null;
  prompt = [];  
  actionName = '';
  linkedSections = [];
  type = null;
  responseOptions = 1;
  temperature = 1;

  constructor(json, deal) {
    this.deal = deal;

    this.actionName = get(json, 'actionName', 'Generate');
    this.engine = find(AI_ENGINES, { key: json.engine }) || AI_ENGINES[0];
    this.prompt = get(json, 'prompt', DEFAULT_PROMPT);
    this.linkedSections = json.linkedSections ? json.linkedSections.split('|') : [];
    this.type = get(json, 'type', BLOCK_TYPE.DEFAULT);
    this.responseOptions =  get(json, 'responseOptions', 1);
    this.temperature =  get(json, 'temperature', 1);
  }

  get json(){
    return {
      engine: this.engine.key,
      actionName: this.actionName,
      prompt: this.prompt,
      linkedSections: this.linkedSections.length > 0 ? this.linkedSections.join('|') : null,
      type: this.type,
      responseOptions: this.responseOptions,
      temperature: this.temperature
    }
  }

  get dataSourceAI() {
    let table = null, text = null, source = [];

    const children = map(this.linkedSections, (id) => this.deal.sections[id])
    
    find(children, (child) => {

      table = find(child.variables, (v) => {
        return (
          v.valueType === ValueType.TABLE || 
          v.externalType === EXTERNAL_TYPES.COLLECTION
        );
      });

      if (table) return true;
      return false;

    });

    if (table) {
      return table.val;
    }
    else {
      source = children;

      // If we're pointing at a LIST, use that list's children as the source content
      if (source.length > 0 && source[0].isList && !source[0].isAI) {
        source = source[0].items;
      }

      text = map(source, (sec) => sec.currentVersion.getText('body', true, this.deal.variables)).join('\n');
      return text.trim() || null;
    }
  }

}